<template>
  <div id="mobile-menu">
    <TheHamburger :is-active="isActive" @click.native="toggle" />

    <transition name="fade">
      <div v-if="isActive" class="mobile-container">
        <nav id="mobile-navigation">
          <template v-for="item in navigationList">
            <router-link
              v-if="!item.external"
              :key="item.slug"
              :to="item.link"
              class="link"
              :class="{ 'dropdown': item.dropdown }"
              @click.native="toggle">
              <span class="icon">
                <i class="far" :class="item.icon"></i>
              </span>
              <span class="title">{{ item.name }}</span>
            </router-link>

            <a v-else
              :key="item.slug"
              :href="item.link"
              class="link"
              :class="{ 'dropdown': item.dropdown }">
              <span class="icon">
                <i class="far" :class="item.icon"></i>
              </span>
              <span class="title">{{ item.name }}</span>
            </a>
          </template>

          <router-link
            v-if="!isAuthenticated"
            to="/login"
            class="link"
            @click.native="toggle">
            <span class="icon">
              <i class="far fa-user-circle"></i>
            </span>
            <span class="title">{{ $t('general.login_signup') }}</span>
          </router-link>

          <router-link
            v-else
            to="/dashboard"
            class="link"
            @click.native="toggle">
            <span class="icon">
              <i class="far fa-user-circle"></i>
            </span>
            <span class="title">
              <template v-if="member && member.name">
                {{ member.name }}
              </template>
              <template v-else-if="member && member.email">
                {{ member.email }}
              </template>
              <template v-else>
                Dashboard
              </template>
            </span>
          </router-link>
        </nav>

        <div id="mobile-footer">
          <p>{{ siteName }}</p>

          <ul class="list-reset">
            <li v-for="item in menuList" :key="item.slug">
              <router-link :to="item.link" @click.native="toggle">
                {{ item.name }}
              </router-link>
            </li>
          </ul>

          <p>Follow Us</p>

          <div class="social-links">
            <a v-for="(item, index) in social"
              :key="index"
              :href="item.link"
              target="_blank"
              rel="noreferrer noopener"
              :title="`PilihLaptop.com at ${item.name}`"
              class="link-social">
              <i :class="`fab fa-${item.icon}`"></i>
              <span class="sr-only">{{ item.name }} PilihLaptop.com</span>
            </a>
          </div>

          <p>
            <span class="link-text" :class="isCurrentLocale('en')" @click="setLocale('en')">EN</span> |
            <span class="link-text" :class="isCurrentLocale('id')" @click="setLocale('id')">ID</span>
          </p>

          <p v-html="copyright" class="copy" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CommonMixin from '@/mixins/Common';
import TheHamburger from '@/components/common/TheHamburger.vue';

export default {
  name: 'TheMenu',

  mixins: [CommonMixin],

  components: {
    TheHamburger,
  },

  data() {
    return {
      isActive: false,
      social: [
        {
          name: 'Facebook',
          icon: 'facebook',
          link: 'https://www.facebook.com/PilihLaptopID/',
        },
        {
          name: 'Twitter',
          icon: 'twitter',
          link: 'https://twitter.com/PilihLaptop',
        },
        {
          name: 'Instagram',
          icon: 'instagram',
          link: 'https://instagram.com/pilihlaptop',
        },
      ],
    };
  },

  computed: {
    isAuthenticated() {
      return this.$store.state.user.token !== null;
    },

    member() {
      return this.$store.state.user.member;
    },

    navigationList() {
      return [
        {
          name: this.$t('general.home'),
          slug: 'home',
          link: '/',
          icon: 'fa-home-alt',
          external: false,
        },
        {
          name: this.$t('general.finder'),
          slug: 'finder',
          link: '/product-finder',
          icon: 'fa-server',
          external: false,
        },
        {
          name: this.$t('general.compare'),
          slug: 'compare',
          link: '/compare',
          icon: 'fa-bring-forward',
          external: false,
        },
        {
          name: this.$t('general.news'),
          slug: 'news',
          link: 'https://pilihlaptop.com/news',
          icon: 'fa-newspaper',
          external: true,
        },
      ];
    },

    menuList() {
      return [
        {
          name: this.$t('general.about_us'),
          slug: 'about',
          link: '/about',
        },
        {
          name: this.$t('general.contact_us'),
          slug: 'contact',
          link: '/contact',
        },
        {
          name: this.$t('general.privacy_policies'),
          slug: 'privacy-policies',
          link: '/privacy-policy',
        },
        {
          name: this.$t('general.terms_conditions'),
          slug: 'terms-condition',
          link: '/terms-condition',
        },
      ];
    },

    year() {
      return new Date().getFullYear();
    },

    siteName() {
      return process.env.VUE_APP_SITENAME || 'PilihLaptop.com';
    },

    copyright() {
      return `Copyright &copy; ${this.year} ${this.siteName}`;
    },

    locale() {
      return this.$store.state.locale;
    },
  },

  methods: {
    toggle() {
      this.isActive = !this.isActive;
      this.lockViewport();
    },

    lockViewport() {
      if (this.isActive) {
        document.body.classList.add('locked');
      } else {
        document.body.classList.remove('locked');
      }
    },

    setLocale(locale) {
      this.$i18n.locale = locale;
      this.$day.locale(locale);
      this.$store.commit('SET_LOCALE', locale);
      document.querySelector('html').setAttribute('lang', locale);
    },

    isCurrentLocale(locale) {
      return (this.locale === locale) ? 'current' : '';
    },
  },
};
</script>

<style lang="scss" scoped>
  #mobile-menu {
    color: $white;

    .mobile-container {
      position: absolute;
      top: 60px;
      left: 0;
      height: calc(100vh - 60px);
      width: 100%;
      overflow-y: auto;
      background-color: $black;
    }

    #mobile-navigation {
      .link {
        display: flex;
        align-items: center;
        background-color: #2A2A2A;
        color: $white;

        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 3.5rem;
          width: 4rem;
          background-color: #383838;

          svg {
            height: 1.5rem;
            width: 1.5rem;
          }
        }

        .title {
          padding: 0 1.5rem;
          width: calc(100% - 4rem);
          text-transform: uppercase;
          line-height: 1;
          font-size: 0.875rem;
        }
      }
    }

    #mobile-footer {
      position: relative;
      padding: 1.5rem;
      min-height: calc(100vh - 60px - 280px);
      border-left: 4rem solid #272727;
      background-color: #181818;

      p {
        margin: 0.5rem 0;
        font-size: 0.75rem;
        font-weight: 600;
        color: $grey-medium;

        &:first-child {
          margin-top: 0;
        }
      }

      a {
        display: block;
        padding: 0.25rem 0;
        font-size: 13px;
        color: $white;
      }

      ul {
        margin-bottom: 2rem;
      }

      .social-links {
        display: flex;
        align-items: center;
        justify-content: flex-start;

        .link-social {
          margin: 0.5rem 1.5rem 0.5rem 0;
          text-align: center;

          .svg-inline--fa {
            height: 1.25rem;
            width: 1.25rem;
          }
        }
      }

      .link-text.current {
        color: $white;
      }

      .copy {
        margin: 0.5rem 0 0 0;
        font-size: 0.75rem;
        font-weight: normal;
        color: $grey-medium;
      }
    }
  }
</style>
